import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface RocheLimitProps {
  width?: number;
  height?: number;
}

export const RocheLimit: React.FC<RocheLimitProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);
  const [distance, setDistance] = useState(80); // 0 (center of planet) to 100 (far left)
  const [shattered, setShattered] = useState(false);

  const rocheLimitPerc = 40;
  
  // State for particles when shattered
  const particlesRef = useRef<{x: number, y: number, vx: number, vy: number}[]>([]);

  useEffect(() => {
    if (distance < rocheLimitPerc && !shattered) {
      setShattered(true);
      // Generate particles based on current moon position
      const padding = 20;
      const trackWidth = size - padding * 2;
      const planetX = size - 60;
      const moonX = planetX - (distance / 100) * trackWidth;
      const centerY = size / 2;
      
      const newParticles = [];
      for (let i = 0; i < 40; i++) {
        newParticles.push({
          x: moonX + (Math.random() - 0.5) * 20,
          y: centerY + (Math.random() - 0.5) * 20,
          vx: (Math.random() - 0.5) * 2 + 1, // explode towards planet
          vy: (Math.random() - 0.5) * 4
        });
      }
      particlesRef.current = newParticles;
    }
  }, [distance, shattered, size]);

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerY = size / 2;
    const padding = 20;
    const trackWidth = size - padding * 2;
    
    const planetX = size - 60;
    const rocheLimitX = planetX - (rocheLimitPerc / 100) * trackWidth;
    
    // Draw Roche Limit Line
    ctx.beginPath();
    ctx.strokeStyle = '#EF4444';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.moveTo(rocheLimitX, 20);
    ctx.lineTo(rocheLimitX, size - 20);
    ctx.stroke();
    ctx.setLineDash([]);
    
    ctx.fillStyle = '#EF4444';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('ROCHE LIMIT', rocheLimitX, 15);

    // Draw Planet
    ctx.fillStyle = '#C88B3A'; // Jupiter-ish color
    ctx.beginPath();
    ctx.arc(planetX, centerY, 50, 0, Math.PI * 2);
    ctx.fill();

    // Calculate Moon Position
    const moonX = planetX - (distance / 100) * trackWidth;

    if (!shattered) {
      // Draw intact or stretching moon
      ctx.save();
      ctx.translate(moonX, centerY);
      
      // Rotate slowly if intact
      if (distance > rocheLimitPerc + 5) {
        ctx.rotate(frameCount * 0.02);
      }
      
      ctx.fillStyle = '#94A3B8';
      ctx.beginPath();
      
      if (distance <= rocheLimitPerc + 10) {
        // Stretching near the limit
        const stretch = 1 + (rocheLimitPerc + 10 - distance) / 20;
        ctx.ellipse(0, 0, 15 * stretch, 15 / stretch, 0, 0, Math.PI * 2);
      } else {
        // Round
        ctx.arc(0, 0, 15, 0, Math.PI * 2);
      }
      
      ctx.fill();
      
      // Craters
      ctx.fillStyle = '#64748B';
      ctx.beginPath();
      ctx.arc(-5, -5, 3, 0, Math.PI*2);
      ctx.arc(4, 5, 4, 0, Math.PI*2);
      ctx.arc(-6, 6, 2, 0, Math.PI*2);
      ctx.fill();
      
      ctx.restore();
    } else {
      // Draw shattered particles
      ctx.fillStyle = '#94A3B8';
      particlesRef.current.forEach(p => {
        // Simple orbital physics around planet
        const dx = planetX - p.x;
        const dy = centerY - p.y;
        const distSq = dx*dx + dy*dy;
        const dist = Math.sqrt(distSq);
        
        if (dist > 50) {
          const force = 100 / distSq;
          p.vx += (dx / dist) * force;
          p.vy += (dy / dist) * force;
        } else {
          // If hit planet surface, drag it along surface
          p.vx *= 0.9;
          p.vy *= 0.9;
        }

        p.x += p.vx;
        p.y += p.vy;

        ctx.beginPath();
        ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
        ctx.fill();
      });
      
      ctx.fillStyle = '#EF4444';
      ctx.font = 'bold 16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('ROCHE LIMIT CROSSED — Moon shatters!', size / 2, size - 30);
    }

  }, [size, distance, shattered]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size - 80, position: 'relative', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E293B' }}>
        <canvas ref={canvasRef} />
      </div>
      
      <div style={{ marginTop: '20px', width: '100%', maxWidth: '300px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '14px', color: '#94A3B8' }}>Moon Distance</label>
          {shattered && (
            <button 
              onClick={() => { setDistance(80); setShattered(false); }}
              style={{ background: '#334155', color: 'white', border: 'none', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' }}>
              Reset
            </button>
          )}
        </div>
        <input 
          type="range" 
          min="10" 
          max="100" 
          value={distance} 
          onChange={(e) => {
            if (shattered) return; // Lock if shattered until reset
            setDistance(Number(e.target.value));
          }}
          disabled={shattered}
          style={{ width: '100%', cursor: shattered ? 'not-allowed' : 'pointer' }}
        />
      </div>
    </div>
  );
};
